import { useMemo, useState } from "react";
import clsx from "clsx";
import type { Tag, TripNote } from "@poi-planner/shared";
import { rankNoteTags } from "../utils/noteTags";
import { TagIcon } from "./TagIcon";
import { Button } from "./Button";

const INITIAL_TAG_COUNT = 6;

function TagChoice({
  tag,
  selected,
  onClick,
  disabled,
}: {
  tag: Tag;
  selected: boolean;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <Button
      variant="unstyled"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-1 disabled:cursor-default",
        selected
          ? "border-sky-200 bg-sky-50 text-blue-800"
          : "border-border bg-white text-secondary hover:border-sky-300 hover:bg-sky-50 hover:text-blue-800",
      )}
    >
      <TagIcon icon={tag.icon} size={13} />
      <span>{tag.name}</span>
      {selected && !disabled && (
        <span className="material-symbols-outlined text-sm" aria-hidden>
          close
        </span>
      )}
    </Button>
  );
}

export function NoteTagPicker({
  tags,
  notes,
  selectedIds,
  onChange,
  disabled = false,
}: {
  tags: readonly Tag[];
  notes: readonly TripNote[];
  selectedIds: string[];
  onChange: (tagIds: string[]) => void;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const ranked = useMemo(
    () => rankNoteTags(tags, notes, selectedIds),
    [notes, selectedIds, tags],
  );
  const selectedTags = ranked.filter((tag) => selectedSet.has(tag.id));
  const availableTags = ranked.filter((tag) => !selectedSet.has(tag.id));
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleAvailable = normalizedQuery
    ? availableTags.filter((tag) =>
        tag.name.toLocaleLowerCase().includes(normalizedQuery),
      )
    : expanded
      ? availableTags
      : availableTags.slice(0, INITIAL_TAG_COUNT);

  const toggleTag = (tagId: string) => {
    onChange(
      selectedSet.has(tagId)
        ? selectedIds.filter((id) => id !== tagId)
        : [...selectedIds, tagId],
    );
  };

  if (tags.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border px-4 py-5 text-center text-sm font-semibold text-secondary">
        No tags available yet.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      {selectedTags.length > 0 && (
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-2xs font-extrabold uppercase tracking-wider text-tertiary">
              Selected
            </span>
            <span className="text-xs font-bold text-secondary">
              {selectedTags.length}
            </span>
          </div>
          <div
            className="flex flex-wrap gap-2"
            data-testid="selected-note-tags"
          >
            {selectedTags.map((tag) => (
              <TagChoice
                key={tag.id}
                tag={tag}
                selected
                disabled={disabled}
                onClick={() => toggleTag(tag.id)}
              />
            ))}
          </div>
        </div>
      )}

      <label className="form-label" htmlFor="note-tag-search">
        Find a tag
      </label>
      <div className="relative">
        <span
          className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-tertiary"
          aria-hidden
        >
          search
        </span>
        <input
          id="note-tag-search"
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search all tags…"
          disabled={disabled}
          className="input-base pl-10 pr-9"
        />
        {query && !disabled && (
          <Button
            variant="icon"
            size="sm"
            icon="close"
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            aria-label="Clear tag search"
          />
        )}
      </div>

      <div
        className="mt-3 flex max-h-36 flex-wrap content-start gap-2 overflow-y-auto custom-scrollbar"
        data-testid="available-note-tags"
      >
        {visibleAvailable.map((tag) => (
          <TagChoice
            key={tag.id}
            tag={tag}
            selected={false}
            disabled={disabled}
            onClick={() => toggleTag(tag.id)}
          />
        ))}
        {visibleAvailable.length === 0 && (
          <span className="py-2 text-xs font-semibold text-tertiary">
            {normalizedQuery ? "No matching tags" : "All tags selected"}
          </span>
        )}
      </div>

      {!normalizedQuery && availableTags.length > INITIAL_TAG_COUNT && (
        <Button
          variant="unstyled"
          onClick={() => setExpanded((current) => !current)}
          disabled={disabled}
          className="mt-3 text-xs font-bold text-info hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 disabled:text-tertiary"
        >
          {expanded
            ? "Show relevant tags"
            : `Browse all ${availableTags.length} tags`}
        </Button>
      )}
    </div>
  );
}
